import React, { useState } from "react";
import { X, Mail, User, Phone } from "lucide-react";
import "./ContactInfoModal.css";

const ContactInfoModal = ({
  isOpen,
  onClose,
  onSubmit,
  eventTitle,
  loading,
  onLoginRedirect,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (formData.phone.trim() && !/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phoneNumber: formData.phone.trim(),
      });
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-info-modal-overlay" onClick={handleBackdropClick}>
      <div className="contact-info-modal-container">
        <div className="contact-info-modal-handle-container">
          <div className="contact-info-modal-handle"></div>
        </div>

        <button
          className="contact-info-modal-close-btn"
          onClick={handleClose}
          disabled={loading}
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        <div className="contact-info-modal-header">
          <h2 className="contact-info-modal-title">Contact Information</h2>
          <p className="contact-info-modal-description">
            Kindly input your contact details to continue with this process.
          </p>
        </div>

        <div className="contact-info-modal-body">
          <form onSubmit={handleSubmit} className="contact-info-form">
            <div className="contact-info-form-group">
              <label htmlFor="firstName" className="contact-info-form-label">
                First Name <span className="contact-info-required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                className={`contact-info-form-input ${errors.firstName ? "contact-info-error" : ""}`}
                disabled={loading}
              />
              {errors.firstName && (
                <span className="contact-info-error-message">
                  {errors.firstName}
                </span>
              )}
            </div>

            <div className="contact-info-form-group">
              <label htmlFor="lastName" className="contact-info-form-label">
                Last Name <span className="contact-info-required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                className={`contact-info-form-input ${errors.lastName ? "contact-info-error" : ""}`}
                disabled={loading}
              />
              {errors.lastName && (
                <span className="contact-info-error-message">
                  {errors.lastName}
                </span>
              )}
            </div>

            <div className="contact-info-form-group">
              <label htmlFor="email" className="contact-info-form-label">
                Email <span className="contact-info-required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className={`contact-info-form-input ${errors.email ? "contact-info-error" : ""}`}
                disabled={loading}
                autoCapitalize="none"
              />
              {errors.email && (
                <span className="contact-info-error-message">
                  {errors.email}
                </span>
              )}
            </div>

            <div className="contact-info-form-group">
              <label htmlFor="phone" className="contact-info-form-label">
                Phone Number{" "}
                <span className="contact-info-optional">(Optional)</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className={`contact-info-form-input ${errors.phone ? "contact-info-error" : ""}`}
                disabled={loading}
              />
              {errors.phone && (
                <span className="contact-info-error-message">
                  {errors.phone}
                </span>
              )}
            </div>

            <div className="contact-info-form-actions">
              <button
                type="button"
                onClick={handleClose}
                className="contact-info-btn-back"
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                className="contact-info-btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="contact-info-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>

            {onLoginRedirect && (
              <p className="contact-info-modal-login-prompt">
                Already have an account?{" "}
                <button
                  type="button"
                  className="contact-info-modal-login-link"
                  onClick={onLoginRedirect}
                  disabled={loading}
                >
                  Log in
                </button>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactInfoModal;
